import { FC } from "react";

export type BillingPeriod = "monthly" | "yearly";

const options: { value: BillingPeriod; unit: string; note: string }[] = [
  { value: "monthly", unit: "month", note: "Billed monthly. Cancel anytime" },
  { value: "yearly", unit: "year", note: "Billed yearly. Cancel anytime." },
];

const gradientBg =
  "bg-[linear-gradient(180deg,rgba(232,203,152,0.069)_0%,rgba(246,242,255,0.0138)_100%)]";

type Props = {
  value: BillingPeriod;
  onChange: (value: BillingPeriod) => void;
  prices: Record<BillingPeriod, number>;
};

// Two equal halves with the text fixed in place; only the outline slides, and
// the radio dot fades with it. Nothing resizes, so nothing can shift or wrap
// differently mid-transition.
const BillingPeriodSelector: FC<Props> = ({ value, onChange, prices }) => (
  <div
    role="radiogroup"
    className={`relative grid grid-cols-2 rounded-full ${gradientBg}`}
  >
    <span
      aria-hidden
      className={`pointer-events-none absolute inset-y-0 left-0 w-1/2 rounded-full border-[1.5px] border-white transition-transform duration-300 ease-out ${gradientBg} ${
        value === "yearly" ? "translate-x-full" : "translate-x-0"
      }`}
    />
    {options.map((option) => {
      const selected = value === option.value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(option.value)}
          className="relative z-10 flex min-w-0 items-center gap-2 rounded-full px-3 py-2 text-left [-webkit-tap-highlight-color:transparent] outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          <span
            className={`flex size-4 shrink-0 items-center justify-center rounded-full border-2 border-white transition-opacity duration-300 ${
              selected ? "opacity-100" : "opacity-0"
            }`}
          >
            <span className="size-2 rounded-full bg-[#CFCFCF]" />
          </span>
          <span className="min-w-0">
            <span
              className={`block text-sm leading-5 font-medium transition-colors duration-300 ${
                selected ? "text-white" : "text-white/45"
              }`}
            >
              ${prices[option.value]} / {option.unit}
            </span>
            <span
              className={`block text-[8px] leading-3 transition-colors duration-300 ${
                selected ? "text-white" : "text-white/35"
              }`}
            >
              {option.note}
            </span>
          </span>
        </button>
      );
    })}
  </div>
);

export default BillingPeriodSelector;
